import { Box } from "@mui/material";
import * as React from "react";
import * as taskService from "../../../../models/services/taskService";
import { Translations } from "ui/Scripts/source/models/translations";
import { ActionContainer } from "../../../framework/ActionContainer";
import { Dialog } from "../../../framework/Dialog/Dialog";
import { ListSearchFilter } from "../../../framework/List/ListSearchFilter";
import { useEffect } from "react";
import { AppUtils } from "source/framework/appUtils";
import { ITaskTypeListItem } from "source/models/taskType/taskTypeListItem";
import { IUnitModuleNode } from "source/models/unit/unitModuleNode";
import { TTaskTypeUnitGroup } from "source/models/taskType/taskTypeUnitGroup";
import { TaskTypesByUnitsDialogTable } from "./TaskTypesByUnitsDialogTable";

interface ITaskTypesByUnitsDialogProp {
    companyId: string;
    onCancel: () => void;
}

interface ITaskTypesByUnitsDialogState {
    unitModuleNode: IUnitModuleNode;
    taskTypes: ITaskTypeListItem[];
    group: TTaskTypeUnitGroup;
    filter: string;
}

export const TaskTypesByUnitsDialog = (props: ITaskTypesByUnitsDialogProp) => {
    const [state, setState] = React.useState<ITaskTypesByUnitsDialogState>({
        unitModuleNode: null,
        group: null,
        filter: "",
        taskTypes: []
    });

    const getUnitTaskTypes = (unitModuleNode: IUnitModuleNode, taskTypes: ITaskTypeListItem[], filter: string) => {
        const result: ITaskTypeListItem[] = [];
        const lowerCaseFilter = filter.toLocaleLowerCase();
        for (const taskType of taskTypes) {
            if (lowerCaseFilter && taskType.code.toLowerCase().indexOf(lowerCaseFilter) < 0) {
                continue;
            }
            if (!taskType.unitIds.length || taskType.unitIds.includes(unitModuleNode.id)) {
                result.push(taskType);
            }
        }
        return result;
    };

    const getGroup = (unitModuleNode: IUnitModuleNode, taskTypes: ITaskTypeListItem[], filter: string): TTaskTypeUnitGroup => {
        if (unitModuleNode.getIsDisabled()) return null;
        const unitTaskTypes = getUnitTaskTypes(unitModuleNode, taskTypes.filter(i => i.activeState > 0.5), filter);
        const result: TTaskTypeUnitGroup = {
            id: unitModuleNode.id,
            name: unitModuleNode.name,
            children: [],
            taskTypes: unitTaskTypes,
        };
        for (const child of unitModuleNode.children) {
            const childGroup = getGroup(child, taskTypes, filter);
            if (!childGroup) continue;
            result.children.push(childGroup);
        }
        if (filter && !unitTaskTypes.length && !result.children.length) return null;
        return result;
    };

    useEffect(() => {
        const loadData = async () => {
            const units = await AppUtils.callService(() => taskService.getUnits(props.companyId));
            if (!units) return;
            const taskTypes = await AppUtils.callService(() => taskService.getTaskTypeListItems(props.companyId, 10000, 1, null, null, true, null));
            if (!taskTypes) return;
            const group = getGroup(units.unitModuleNode, taskTypes.items, state.filter);
            if (!group) return;
            setState(old => ({
                ...old,
                unitModuleNode: units.unitModuleNode,
                taskTypes: taskTypes.items,
                group
            }));
        };

        loadData();
    }, []);

    useEffect(() => {
        if (!state.unitModuleNode) return;
        setState(old => ({
            ...old,
            group: getGroup(state.unitModuleNode, state.taskTypes, state.filter)
        }));
    }, [state.filter]);

    if (!state.taskTypes.length) return null;

    return (
        <Dialog
            id="taskTypesByUnitsDialog"
            maxWidth="md"
            title={Translations.TaskTypesByUnits}
            centerOnResize
            body={
                <Box sx={{ margin: "-1rem 0" }}>
                    <ActionContainer leftItems={
                        <ListSearchFilter
                            inline={true}
                            active={!!state.filter}
                            searchFilter={state.filter}
                            onSearchClick={(filter: string) => setState(old => ({ ...old, filter }))}
                        />
                    } />
                    <Box sx={{ maxHeight: "calc(100vh - 400px)", overflowY: "auto", marginRight: "-1.5rem", paddingRight: "1rem" }}>
                        <TaskTypesByUnitsDialogTable
                            group={state.group}
                            parentName=""
                        />
                    </Box>
                </Box>
            }
            buttons={[
                { title: Translations.Close, variant: "outlined", onClick: props.onCancel }
            ]}
            onClose={props.onCancel}
        />
    );
};